import {messages} from '@smartmixin/i18n';
import {CaretRight, Question} from '@phosphor-icons/react';
import {
  alpha,
  Box,
  ButtonBase,
  Grid,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router';
import AppPaper from '../../components/AppPaper';
import RouterLink from '../../lib/RouterLink';
import {
  CATEGORY_COLORS,
  CATEGORY_ICONS,
  CATEGORY_MESSAGES,
  CATEGORY_ORDER,
  getReadingTime,
} from '../constants';
import {useArticles} from '../hooks/useArticles';
import {useHelpBasePath} from '../hooks/useHelpLocale';
import {Article} from '../types';

type ArticleCardProps = {
  article: Article;
};

const ArticleCard = ({article}: ArticleCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {formatMessage} = useIntl();
  const helpBasePath = useHelpBasePath();
  const readingTime = getReadingTime(article.content);

  return (
    <ButtonBase
      onClick={() => navigate(`${helpBasePath}/${article.slug}`)}
      sx={{
        width: '100%',
        textAlign: 'left',
        borderRadius: 2,
      }}>
      <AppPaper
        sx={{
          p: 2,
          width: '100%',
          height: '100%',
          transition: 'all 200ms ease',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
          },
        }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
          {article.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}>
          {formatMessage(messages.helpCenter.minRead, {minutes: readingTime})}
        </Typography>
      </AppPaper>
    </ButtonBase>
  );
};

type CategoryCardProps = {
  category: string;
  articles: Article[];
  label: string;
};

const CategoryCard = ({category, articles, label}: CategoryCardProps) => {
  const theme = useTheme();
  const {formatMessage} = useIntl();
  const helpBasePath = useHelpBasePath();
  const Icon = CATEGORY_ICONS[category] || Question;
  const color = CATEGORY_COLORS[category] || theme.palette.primary.main;
  const categoryPath = `${helpBasePath}/category/${category}`;

  return (
    <AppPaper sx={{p: 3, height: '100%'}}>
      <Link
        component={RouterLink}
        to={categoryPath}
        underline="none"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          color: 'inherit',
          '&:hover': {
            '& .category-title': {color: 'primary.main'},
            '& .category-chevron': {opacity: 1, transform: 'translateX(4px)'},
          },
        }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            backgroundColor: alpha(color, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
          <Icon size={24} color={color} />
        </Box>
        <Box sx={{flexGrow: 1}}>
          <Typography
            variant="h6"
            className="category-title"
            sx={{fontWeight: 600, transition: 'color 200ms ease'}}>
            {label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}>
            {formatMessage(messages.helpCenter.articleCount, {
              count: articles.length,
            })}
          </Typography>
        </Box>
        <Box
          className="category-chevron"
          sx={{
            display: 'flex',
            color: 'text.secondary',
            opacity: 0.5,
            transition: 'all 200ms ease',
          }}>
          <CaretRight size={20} />
        </Box>
      </Link>
      <Grid container spacing={1.5}>
        {articles.slice(0, 4).map((article) => (
          <Grid key={article.id} size={12}>
            <ArticleCard article={article} />
          </Grid>
        ))}
      </Grid>
      {articles.length > 4 && (
        <Box sx={{mt: 2, textAlign: 'center'}}>
          <Link
            component={RouterLink}
            to={categoryPath}
            underline="hover"
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'primary.main',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}>
            +{articles.length - 4} {formatMessage(messages.common.more)}
            <CaretRight size={16} />
          </Link>
        </Box>
      )}
    </AppPaper>
  );
};

const ArticleList = () => {
  const {articlesByCategory} = useArticles();
  const {formatMessage} = useIntl();

  // Sort categories by predefined order, unknown categories at the end
  const sortedCategories = Object.keys(articlesByCategory).sort((a, b) => {
    const indexA = CATEGORY_ORDER.indexOf(a);
    const indexB = CATEGORY_ORDER.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <Grid data-testid="help-article-list" container spacing={3}>
      {sortedCategories.map((category) => (
        <Grid key={category} size={{xs: 12, md: 6}}>
          <CategoryCard
            category={category}
            articles={articlesByCategory[category]}
            label={
              CATEGORY_MESSAGES[category]
                ? formatMessage(CATEGORY_MESSAGES[category])
                : category
            }
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default ArticleList;
